import { useRefinementList } from 'react-instantsearch';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CustomRefinementList = ({ attribute, showDisclosure = false, gridColumns = 1, title }) => {
    const { t } = useTranslation('common');
    const [showAll, setShowAll] = useState(false);
    const { items, refine } = useRefinementList({
        attribute,
        limit: 50,
        sortBy: ['name:asc'],
    });

    const formatLabel = (value, attribute) => {
        if (attribute === 'bathrooms') {
            return t('listings.filters.plurals.bathrooms', { count: Number(value) });
        }
        if (attribute === 'bedrooms') {
            return t('listings.filters.plurals.bedrooms', { count: Number(value) });
        }
        if (attribute === 'floors') {
            return t('listings.filters.plurals.floors', { count: Number(value) });
        }
        if (attribute === 'private_features' || attribute === 'community_features') {
            // For private features
            if (attribute === 'private_features') {
                return t(`estate.details.features.private.${value}`, {
                    defaultValue: t(`estate.details.features.community.${value}`, {
                        defaultValue: value
                    })
                });
            }
            // For community features
            return t(`estate.details.features.community.${value}`, {
                defaultValue: t(`estate.details.features.private.${value}`, {
                    defaultValue: value
                })
            });
        }
        return value;
    };

    const sortItems = (items, attribute) => {
        return [...items].sort((a, b) => {
            if (attribute === 'bedrooms' || attribute === 'bathrooms') {
                return Number(a.value) - Number(b.value);
            }
            return a.label.localeCompare(b.label, 'da-DK');
        });
    };

    const sortedItems = useMemo(() => sortItems(items, attribute), [items, attribute]);
    const visibleItems = showAll ? sortedItems : sortedItems.slice(0, 10);
    const hasMore = sortedItems.length > 10;

    const FilterContent = () => (
        <div className="pt-6">
            <div className={`grid ${gridColumns === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8 gap-y-3`}>
                {visibleItems.map((item, optionIdx) => (
                    <div key={item.value} className="flex items-center">
                        <input
                            id={`${attribute}-${optionIdx}`}
                            name={`${attribute}[]`}
                            value={item.value}
                            type="checkbox"
                            checked={item.isRefined}
                            onChange={() => refine(item.value)}
                            className="size-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                        />
                        <label
                            htmlFor={`${attribute}-${optionIdx}`}
                            className="ml-3 text-sm text-gray-600 relative w-full"
                        >
                            {formatLabel(item.label, attribute)}
                            <span className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-center text-xs/5 font-medium text-gray-600 ring-1 ring-inset ring-gray-200 right-0 absolute">
                                {item.count}
                            </span>
                        </label>
                    </div>
                ))}
            </div>
            {hasMore && (
                <button
                    type="button"
                    className="tect-center w-full font-semibold mx-auto uppercase mt-6 text-xs text-gray-500 hover:text-sky-500"
                    onClick={() => setShowAll(!showAll)}
                >
                    {showAll
                        ? t('listings.filters.actions.showLess')
                        : t('listings.filters.actions.showAll', { count: sortedItems.length })}
                </button>
            )}
        </div>
    );

    if (showDisclosure) {
        return (
            <Disclosure as="div" className="bg-gray-50 p-5 rounded-lg">
                <fieldset className="bg-gray-50 p-5 rounded-lg">
                    <legend className="w-full px-2">
                        <Disclosure.Button className="group flex w-full items-center justify-between p-2 text-gray-400 hover:text-gray-500">
                            <span className="text-sm font-medium text-gray-900">
                                {t(`listings.filters.attributes.${attribute}`)}
                            </span>
                            <span className="ml-6 flex h-7 items-center">
                                <ChevronDownIcon
                                    className="size-5 rotate-0 transform group-data-[open]:-rotate-180"
                                    aria-hidden="true"
                                />
                            </span>
                        </Disclosure.Button>
                    </legend>
                    <Disclosure.Panel className="px-4 pb-2 pt-4">
                        <FilterContent />
                    </Disclosure.Panel>
                </fieldset>
            </Disclosure>
        );
    }

    return (
        <div className="bg-gray-50 p-5 rounded-lg">
            <fieldset>
                <legend className="block text-sm font-medium text-gray-900">
                    {title || t(`listings.filters.attributes.${attribute}`)}
                </legend>
                <FilterContent />
            </fieldset>
        </div>
    );
};

export default CustomRefinementList;